import { Box, Typography } from '@mui/material'
import React, { useState } from 'react'
import './Calendar.css'
import dashboard_ from '../../sass/dashboard.module.sass'
import { Subscriber, SubscriberStatus } from '../../store/user/reducer'
import { t } from 'i18next'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
  } from '@mui/material'
  import { useDispatch } from 'react-redux'
import { calendarRangeSearch } from '../../store/user/actionCreator'
import { UsageStatsTable } from './UsageStatsTable'

type BadgeMap = Record<string, number>

const daysInMonth = (year: number, month: number): number =>
    new Date(year, month + 1, 0).getDate()

const getToday = () => {
    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, '0')
    const day = String(today.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

interface Props {
    subscriber: Subscriber
  }
  
export function Calendar({ subscriber }: Props) {
    const [dialogOpen, setDialogOpen] = useState(false)
    const [dialogContent, setDialogContent] = useState<any[]>([])
    
    const [initialStatus, setInitialStatus] = React.useState<SubscriberStatus>()
    const dispatch = useDispatch()

    const handleDialogClose = () => {
        setDialogOpen(false)
    }

    const [currentDate, setCurrentDate] = useState(new Date())
    const [dateFrom, setDateFrom] = useState<string | null>(null)
    const [dateTo, setDateTo] = useState<string | null>(null)

    const calendar : any = subscriber.calendar

    const badges: BadgeMap = Object.fromEntries(
        calendar?.data?.map( (item : any ) => [item?.date, Number(item?.count)]),
      )

    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()
    const days = daysInMonth(year, month)
    const startDay = (new Date(year, month, 1).getDay() + 6) % 7

    const handleDateClick = (dateStr: string) => {
        if (!dateFrom || (dateFrom && dateTo)) {
            setDateFrom(dateStr)
            setDateTo(null)
        } else {
            if (new Date(dateStr) < new Date(dateFrom)) {
                setDateTo(dateFrom)
                setDateFrom(dateStr)
            } else {
                setDateTo(dateStr)
            }
        }
    }

    const isInRange = (dateStr: string): boolean => {
        if (!dateFrom || !dateTo) return false
        const d = new Date(dateStr).getTime()
        return (
            d >= new Date(dateFrom).getTime() && d <= new Date(dateTo).getTime()
        )
    }

    const formatDate = (date: Date): string => {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
    }

    const handleSearch = () => {
        if (!dateFrom || !dateTo) return
        dispatch(calendarRangeSearch(
        subscriber.id,
          dateFrom,
          dateTo,
          (data: any) => {
            //console.log("DATA RECEIVED:", data)
            setInitialStatus(data.status)
            setDialogContent(data)
            setDialogOpen(true)
          }))
      }

      const calculateTotal = (data: any[]): number => {
        return data.reduce((sum, entry) => {
          if (entry && typeof entry.count === 'number') {
            return sum + entry.count
          }
          return sum
        }, 0)
      }

    return (
        <>
        <Typography variant="semiBold" fontSize={18}>
        {t('subscriber.usageCalendar')}
      </Typography>

      <Box
        className={dashboard_.card_column}
        mt={1}
        sx={{ padding: '16px !important' }}
      >
        <div className="calendar-container">
            <div className="calendar-header">
                <button onClick={() => setCurrentDate(new Date(year, month - 1, 1))}>{'<'}</button>
                <span>{currentDate.toLocaleString('default', { month: 'long' })} {year}</span>
                <button onClick={() => setCurrentDate(new Date(year, month + 1, 1))}>{'>'}</button>
            </div>

            <div className="calendar-grid">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                    <div key={d} className="day-label">{d}</div>
                ))}

                {Array(startDay).fill(null).map((_, i) => (
                    <div key={`empty-${i - 1}`} className="day empty" />
                ))}

                {Array.from({ length: days }, (_, i) => {
                    const date = new Date(year, month, i + 1)
                    const dateStr = formatDate(date)
                    const isToday = dateStr === getToday()
                    const selected =
                        dateStr === dateFrom || dateStr === dateTo || isInRange(dateStr)

                    return (
                        <div
                            key={dateStr}
                            className={`day${isToday ? ' today' : ''}${selected ? ' selected' : ''}`}
                            onClick={() => handleDateClick(dateStr)}
                        >
                            {i + 1}
                            {badges[dateStr] && (
                                <span className="badge">{badges[dateStr]}</span>
                            )}
                        </div>
                    )
                })}
            </div>

            <div className="calendar-footer">
                <div>From: <b>{dateFrom || '-'}</b></div>
                <div>To: <b>{dateTo || '-'}</b></div>
                <button
                    className="search-button"
                    disabled={!dateFrom || !dateTo}
                    onClick={handleSearch}
                >
                    Search
                </button>
            </div>
        </div>
        </Box>
        <Dialog open={dialogOpen} onClose={handleDialogClose} fullWidth maxWidth="sm">
  <DialogTitle>📊 {dateFrom} - {dateTo} Total results: {calculateTotal(dialogContent)} </DialogTitle> 
  <DialogContent>
  <UsageStatsTable data={dialogContent} />
</DialogContent>
  <DialogActions>
    <Button onClick={handleDialogClose}>Close</Button>
  </DialogActions>
</Dialog>
        </>
    )
}

export default Calendar
